import { useEffect, useMemo, useState } from 'react'
import { fetchDayHour, type DayHourResponse } from '../../lib/api'
import { useFlowFilter } from '../FlowFilterContext'

interface Props { range: { from: number; to: number } }

const DOW_LABELS = ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn']

export function DayHourHeatmap({ range }: Props) {
  const { variantParam, audienceParam } = useFlowFilter()
  const [type, setType] = useState<'sessions' | 'leads'>('sessions')
  const [data, setData] = useState<DayHourResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setData(null); setError(null)
    fetchDayHour(range, variantParam, type, audienceParam)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setError(String(e)) })
    return () => { cancelled = true }
  }, [range.from, range.to, variantParam, type, audienceParam])

  // Build a fast lookup map: dow*24+hour -> n
  const lookup = useMemo<Map<number, number>>(() => {
    const m = new Map<number, number>()
    if (!data) return m
    for (const c of data.cells) m.set(c.dow * 24 + c.hour, c.n)
    return m
  }, [data])

  if (error) return <div className="card card-empty">Kunne ikke hente heatmap-data</div>
  if (!data) return <div className="card card-skeleton" aria-busy />

  const max = Math.max(1, data.max)

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Aktivitet (dag × time)</div>
        <div className="card-subtitle">Mandag øverst, time 0–23 vandret</div>
        <div className="card-toolbar">
          <button
            className={`tab-pill ${type === 'sessions' ? 'active' : ''}`}
            onClick={() => setType('sessions')}
          >Sessioner</button>
          <button
            className={`tab-pill ${type === 'leads' ? 'active' : ''}`}
            onClick={() => setType('leads')}
          >Leads</button>
        </div>
      </div>
      <div className="heatmap">
        <div className="heatmap-row heatmap-header">
          <div className="heatmap-corner" />
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="heatmap-hour-label">{h % 6 === 0 ? h : ''}</div>
          ))}
        </div>
        {DOW_LABELS.map((day, dow) => (
          <div key={day} className="heatmap-row">
            <div className="heatmap-day-label">{day}</div>
            {Array.from({ length: 24 }, (_, hour) => {
              const n = lookup.get(dow * 24 + hour) ?? 0
              const intensity = n / max
              const tooltipAlign = hour < 3 ? 'start' : hour > 20 ? 'end' : 'center'
              return (
                <div
                  key={hour}
                  className="heatmap-cell"
                  data-align={tooltipAlign}
                  data-tooltip={`${day} kl. ${hour.toString().padStart(2, '0')}:00 — ${n} ${type === 'sessions' ? 'sessioner' : 'leads'}`}
                  style={{ '--intensity': intensity } as React.CSSProperties}
                />
              )
            })}
          </div>
        ))}
      </div>
      <div className="heatmap-legend">
        <span className="muted small">0</span>
        <div className="heatmap-legend-bar" />
        <span className="muted small">{max}</span>
      </div>
    </div>
  )
}
